import { useMemo, useState } from 'react';
import { formatJMD } from '../../lib/finances';

// The description field on an invoice line.
//
// Same shape as VendorAutocomplete, and different in one deliberate way: it
// never creates anything. Picking a product fills the price and links the
// line so stock can move when the invoice is paid; typing something the
// catalogue has never heard of is a perfectly good invoice line and is kept
// as free text rather than being silently added to the shop's catalogue.
//
// `onPick` is separate from `onChange` for that reason: the parent has to
// tell "they chose the product" from "they typed something that happens to
// match", because only the first should carry a product_id and a price.
export default function ProductAutocomplete({ value, onChange, onPick, products, placeholder }) {
  const [open, setOpen] = useState(false);

  const query = value.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!query) return products.slice(0, 8);
    return products.filter((p) => p.name.toLowerCase().includes(query)).slice(0, 8);
  }, [products, query]);

  return (
    <div className="product-autocomplete">
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // Same delay VendorAutocomplete uses: a plain blur fires before the
        // click on the option lands, so the list would close out from under
        // the pointer.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <ul className="product-autocomplete-list">
          {matches.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => {
                  onPick(p);
                  setOpen(false);
                }}
              >
                <span className="product-autocomplete-name">{p.name}</span>
                <span className="product-autocomplete-meta">
                  {p.price_jmd != null && formatJMD(p.price_jmd)}
                  {/* Shown because invoicing 14 of something you have 6 of is
                      worth knowing at the moment you type it, not when the
                      stock write clamps at zero later. */}
                  {p.stock_qty != null && <em>{p.stock_qty} in stock</em>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
